import { downloadUrl, PageDropZone, useForm } from '@tempots/beatui'
import { attr, dataAttr, Fragment, html, on, prop } from '@tempots/dom'
import { HTMLTitle } from '@tempots/ui'
import { RESUME_UPLOAD_ACCEPT } from '@rh/shared/constants/resume-content-types'
import { Notify } from '@rh/shared/utils/notify'
import { Track } from '@/services/track'
import {
  customizeOptionsSchema,
  PdfService,
  TrialProcessOptions,
} from '@/services/pdf-service'
import { ApiError } from '@rh/shared/services/auth-service'
import { EphimeralService } from '@/utils/ephimeral'
import { OnboardingInfo } from '@/models/onboarding-info'
import { ResumeState } from '@/models/resume-state'
import { ResumeLivePreview } from '@/components/resume-live-preview'
import { TopBar } from '@/components/top-bar'
import { BrandResumePanel } from '@/components/brand-resume-panel'
import { ResumeDropOverlay } from '@/components/resume-drop-overlay'
import { TrialInputs } from '@/components/trial-inputs'
import { ResumeUploadService } from '@/services/resume-upload-service'
import { LatestResume } from '@/models/latest-resume'
import type { ContentBoundsReport } from '@rh/shared/utils/content-trim'

export interface TrialPageOptions {
  resumeFile: File | null
  setResumeFile: (file: File | null) => void
  onResumeFileChange?: (callback: (file: File | null) => void) => void
}

function normalizeResumeFilename(filename: string, agencyName: string | null) {
  const basename = filename.split('.').slice(0, -1).join('.')
  const suffix = agencyName ?? 'Updated'
  return `${basename} - ${suffix}.pdf`
}

export function TrialPage({
  resumeFile,
  setResumeFile,
  onResumeFileChange,
}: TrialPageOptions) {
  const { resume } = ResumeState.makeResumeState({
    resumeFile,
    setResumeFile,
    trackingOptions: { section: 'trial_form' },
  })
  const resumeInfo = prop<{ hash: string; filename: string } | null>(null)

  // Register callback so app.ts can trigger upload when already on trial page
  if (onResumeFileChange) {
    onResumeFileChange((file: File | null) => {
      ResumeState.upload(file, v => resume.set(v), { section: 'trial_form' })
    })
  }
  const storedOnboarding =
    EphimeralService.retrieve<OnboardingInfo>('onboarding')
  const {
    headerAgencyName = 'Example Agency',
    headerAgencyPhoneNumber = '(555) 555-5555',
    headerAgencyUrl = 'example.com',
    headerAgencyLinkedIn = 'linkedin.com/in/example',
    headerAgencyEmail = 'info@example.com',
    agencyBrandColor = '#52547a',
    headerRecruiterName = 'Your Name',
    headerRecruiterPhoneNumber = '(444) 444-4444',
    headerRecruiterEmail = 'you@example.com',
    headerRecruiterLinkedIn = 'linkedin.com/in/you',
  } = storedOnboarding ?? {
    type: 'onboarding',
  }
  const { controller } = useForm<TrialProcessOptions>({
    schema: customizeOptionsSchema,
    initialValue: {
      // Global values
      agencyBrandColor: agencyBrandColor ?? '',

      // Header configuration
      includeHeader: true,
      headerShape: 'columns-3',

      // Header agency data
      headerAgencyName: headerAgencyName ?? '',
      headerAgencyPhoneNumber: headerAgencyPhoneNumber ?? '',
      headerAgencyUrl: headerAgencyUrl ?? '',
      headerAgencyLinkedIn: headerAgencyLinkedIn ?? '',
      headerAgencyEmail: headerAgencyEmail ?? '',

      // Header recruiter data
      headerRecruiterName: headerRecruiterName ?? '',
      headerRecruiterPhoneNumber: headerRecruiterPhoneNumber ?? '',
      headerRecruiterEmail: headerRecruiterEmail ?? '',
      headerRecruiterLinkedIn: headerRecruiterLinkedIn ?? '',

      // Footer configuration (disabled by default for trial)
      includeFooter: false,
      footerShape: 'columns-1-image',
    },
  })

  // When the visitor has never chosen a brand color (nothing persisted —
  // the initial '#52547a' above is just the built-in placeholder), adopt
  // the default design's authored theme color once it loads, so an
  // admin-uploaded default first shows in its intended colors. The
  // still-at-initial guard keeps a color the visitor typed in the
  // meantime from being overwritten.
  if (storedOnboarding?.agencyBrandColor == null) {
    void PdfService.fetchTrialDefaultDesign().then(result => {
      if (!result || result.source !== 'uploaded') return
      const field = controller.field('agencyBrandColor')
      if (field.signal.value === '#52547a') {
        field.change(result.defaultBrandColor)
      }
    })
  }

  controller.signal.on(data => {
    EphimeralService.store<OnboardingInfo>({
      type: 'onboarding',
      ...data,
    })
  })

  // Buffer and content bounds resolve as one atomic pair so the preview
  // never flashes an untrimmed page and then reflows once bounds arrive.
  // Bounds exist only for uploaded documents; a null report renders the
  // preview untrimmed, matching a composer that has no bounds either.
  const resumeData = resume.mapAsync(
    async (
      res
    ): Promise<{
      buffer: ArrayBuffer
      bounds: ContentBoundsReport | null
    } | null> => {
      try {
        switch (res.type) {
          case 'default':
            return {
              buffer: await (
                await PdfService.fetchDefaultResumePdf()
              ).arrayBuffer(),
              bounds: null,
            }
          case 'uploaded': {
            const [buffer, bounds] = await Promise.all([
              ResumeUploadService.downloadPdfByHash(res.hash).then(f =>
                f.arrayBuffer()
              ),
              PdfService.fetchContentBounds(res.hash),
            ])
            return { buffer, bounds }
          }
          case 'uploading':
            // Only show preview for PDF files during upload
            if (res.file.type === 'application/pdf') {
              return { buffer: await res.file.arrayBuffer(), bounds: null }
            }
            // Non-PDF files: wait for conversion, don't pass raw buffer
            return null
          case 'converting':
            // Conversion in progress, show loading state
            return null
          default:
            return null
        }
      } catch {
        setResumeFile(null)
        return null
      }
    },
    null
  )
  const resumeBuffer = resumeData.map(d => d?.buffer ?? null)
  const resumeContentBounds = resumeData.map(d => d?.bounds ?? null)

  resume.on(state => {
    if (state.type !== 'uploaded') return
    const { hash, filename } = state
    resumeInfo.set(state)
    EphimeralService.store(LatestResume.make(hash, filename))
  })

  const onFileSelect = ResumeState.makeUpload(v => resume.set(v), {
    section: 'trial_form',
  })

  return Fragment(
    HTMLTitle('Try ResumeHeaders - Free Trial'),
    html.div(
      attr.class('flex flex-col h-full overflow-hidden'),
      TopBar({
        logoHref: '/',
        // Compact upload affordance: PageDropZone still accepts drops
        // anywhere on the page, this button is the visible click target.
        end: PageDropZone({
          accept: RESUME_UPLOAD_ACCEPT,
          onDragContent: ResumeDropOverlay,
          onChange: files => onFileSelect(files[0] ?? null),
          onInvalidSelection: () => {
            Notify.warn('Invalid resume file')
            Track.clientError({
              error_code: 'INVALID_FILE_TYPE',
              error_message: 'Invalid resume file type',
            })
          },
          content: ({ selectFiles }) =>
            html.button(
              attr.type('button'),
              attr.id('trial-resume-dropzone'),
              on.click(selectFiles),
              attr.class(
                'bg-[#fffaf8] px-2 sm:px-3 py-1.5 rounded-lg border-2 border-dashed border-gray-300 text-xs sm:text-sm font-semibold text-teal-700 cursor-pointer hover:bg-gray-100 hover:border-teal-700 hover:text-teal-900 transition-colors whitespace-nowrap'
              ),
              html.span(
                dataAttr('testid', 'trial-dropzone-text'),
                html.span(attr.class('hidden sm:inline'), 'Drop or click to '),
                'upload resume'
              )
            ),
        }),
      }),
      // Main content
      html.div(
        attr.class(
          'grid grid-cols-1 grid-rows-[auto_1fr] lg:grid-cols-[1fr_1fr] lg:grid-rows-1 h-full overflow-hidden flex-grow gap-0'
        ),
        // Left pane
        html.div(
          attr.class('overflow-hidden flex flex-col max-h-80 lg:max-h-full'),
          html.div(
            attr.class('flex-grow overflow-hidden'),
            TrialInputs({ controller })
          )
        ),
        // Right pane
        html.div(
          attr.class(
            'overflow-hidden flex flex-col min-h-0 md:border-l md:border-gray-200'
          ),
          ResumeLivePreview({
            notAsked: () => BrandResumePanel({ setResumeFile }),
            values: controller.signal,
            resume: resumeBuffer,
            contentBounds: resumeContentBounds,
            resumeState: resume,
            onDownload: async ({ values, setDownloading, setDownloaded }) => {
              setDownloading(true)
              const info = resumeInfo.value
              if (info == null) {
                setDownloading(false)
                return
              }
              try {
                const { hash, filename } = info
                const response = await PdfService.processTrialResume(
                  hash,
                  values
                )

                downloadUrl(
                  response.url,
                  normalizeResumeFilename(
                    filename,
                    values.headerAgencyName ?? null
                  )
                )
                setDownloaded(true)
              } catch (error) {
                if (
                  error instanceof ApiError &&
                  (error.status === 410 || error.status === 409)
                ) {
                  EphimeralService.remove('latestResume')
                  resume.set(ResumeState.notAsked)
                  resumeInfo.set(null)
                  return
                }
                throw error
              } finally {
                setDownloading(false)
              }
            },
          })
        )
      )
    )
  )
}
